import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, SimpleWizard, SimpleWizardStep, SimpleWizardConfirmStep, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { sendChangeRequestBuyerOffer, setChangeRequestOptionOverrides } from '@/services/change-requests';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import {
    ChangeRequest,
    ChangeRequestParticipantInfo,
    BuyerOptionGroupOverride,
    OptionGroupOverrideSelection,
    ChangeRequestProgressInfoOffer,
    BuyerOfferStatus,
    BuyerOfferLine,
} from '@/types';
import Banner from '@/components/Banner';
import theme from './ChangeRequestSendDialog.module.scss';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import ChangeRequestOverrideOptionSelect from '../ChangeRequestOverrideOptionSelect';
import BuyerChangeRequestOffer from '@/buyer/components/BuyerChangeRequests/BuyerChangeRequestOffer';
import { sumBy } from 'lodash-es';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { SendProgress, PromiseCheckList, PromiseCheckListItem, withProgressUpdates } from '@/components/PromiseCheckList';
import { useMergeState } from '@/utils/hooks';
import Tags from '@/components/Tags';
import { SlideToggle } from '@ygtechnologies/rubus-ui';

enum Step {
    AddOverride,
    AddTags,
    AddOfferMessage,
    PreviewAndSend,
    Confirm,
}

const Steps = [Step.AddOverride, Step.AddTags, Step.AddOfferMessage, Step.PreviewAndSend, Step.Confirm];

interface ChangeRequestSendDialogProps {
    changeRequest: ChangeRequest;
    show: boolean;
    onClose(): void;
    onSave(args: { optionGroupOverrides: BuyerOptionGroupOverride[] }): void;
    participants: ChangeRequestParticipantInfo[];
}

type SaveProgress = {
    tags: SendProgress;
    options: SendProgress;
    offer: SendProgress;
};

function getInitialSaveProgress(): SaveProgress {
    return {
        offer: SendProgress.NotStarted,
        options: SendProgress.NotStarted,
        tags: SendProgress.NotStarted,
    };
}

const ChangeRequestSendDialog: React.FC<ChangeRequestSendDialogProps> = ({
    show,
    onClose,
    onSave,
    changeRequest,
    participants,
}) => {
    const defaultShowSubtotalsToBuyers = useSelector(
        (state: IState) => state.global.activeProject?.defaultShowOfferSubtotalsToBuyers ?? false
    );
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);
    const [showSubtotalsToBuyers, setShowSubtotalsToBuyers] = useState(defaultShowSubtotalsToBuyers);
    const [selectedOverrides, setSelectedOverrides] = useState<OptionGroupOverrideSelection[]>([]);
    const [currentStep, setCurrentStep] = useState(Step.AddOverride);

    const [saveProgress, updateSaveProgress, setSaveProgress] = useMergeState<SaveProgress>(getInitialSaveProgress());

    const profile = useSelector((state: IState) => state.global.activeProfile);
    const user = useSelector((state: IState) => state.global.user);

    const handleOpen = async () => {
        setCurrentStep(Steps[0]);
        setSaveProgress(getInitialSaveProgress());
        setSelectedOverrides(changeRequest.optionGroupOverrides.map((x) => ({ optionGroupId: x.optionGroupId, roomId: x.roomId })));
        setNote('');
    };

    const handleSaveClick = async () => {
        setSending(true);
        setSaveProgress(getInitialSaveProgress());

        try {
            // await withProgressUpdates(
            //     setChangeRequestTags(changeRequest.id, tags),
            //     (progress) => updateSaveProgress({ tags: progress }),
            //     250
            // );

            const optionGroupOverrides = await withProgressUpdates(
                setChangeRequestOptionOverrides(changeRequest.id, selectedOverrides),
                (progress) => updateSaveProgress({ options: progress }),
                250
            );

            await withProgressUpdates(
                sendChangeRequestBuyerOffer(changeRequest.id, {
                    note,
                    showSubtotalsToBuyers,
                }),
                (progress) => updateSaveProgress({ offer: progress }),
                250
            );

            triggerHandler(onSave, { optionGroupOverrides });
        } catch (e: any) {
            //Errors will be shown in the save progress
        } finally {
            setSending(false);
        }
    };

    const handleFinish = () => {
        triggerHandler(onClose);
    };

    const preventClose = sending;

    const handleCancelClick = preventClose
        ? undefined
        : () => {
              triggerHandler(onClose);
          };

    const getOfferLines = (participants: ChangeRequestParticipantInfo): BuyerOfferLine[] | null => {
        if (showSubtotalsToBuyers) {
            if (!participants.offerLines) {
                return [];
            }
            return participants.offerLines.map((x) => ({
                id: x.id,
                description: x.description,
                finalPrice: x.priceCalculation!.finalPrice,
                basePrice: null,
                feeSetInfo: null,
                itemCategoryId: x.itemCategoryId,
            }));
        }
        return null;
    };

    const buyerOffer: ChangeRequestProgressInfoOffer = {
        id: 1234,
        note,
        groups: participants.map((p) => ({
            note: p.offerNote || '',
            attachments: p.offerAttachments.filter((x) => !x.hideFromBuyer),
            price: showSubtotalsToBuyers ? p.offerFinalPrice! : null,
            providedByProfile: { id: p.profileId, name: p.profileName },
            providedByUser: { id: p.userId, name: p.displayName },
            offerLines: getOfferLines(p),
            showSubtotalsToBuyers,
        })),
        sendDate: new Date(),
        sentByProfile: { id: profile!.id, name: profile!.name },
        sentByUser: { id: user!.userId, name: user!.displayName },
        status: BuyerOfferStatus.Pending,
        totalPrice: sumBy(
            participants!.flatMap((x) => x.offerLines),
            (x) => x?.priceCalculation?.finalPrice ?? 0
        ),
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen} size="lg">
            <ModalHeader>
                <Trans>Send offer to buyer</Trans>
            </ModalHeader>
            <ModalBody>
                <SimpleWizard
                    saveButtonProps={{ children: <Trans>Send offer</Trans> }}
                    onCancel={handleCancelClick}
                    onSubmit={handleSaveClick}
                    onFinish={handleFinish}
                    finishButtonProps={{ disabled: preventClose }}
                    steps={Steps}
                    currentStep={currentStep}
                    onStepChange={(step) => setCurrentStep(step)}
                    waitForSubmit={false}>
                    <SimpleWizardStep stepNumber={Step.AddOverride} header={<Trans>Add an override</Trans>}>
                        <p>
                            <Trans>
                                Please make sure that all options that should be overridden by this change request have been
                                selected.
                            </Trans>
                        </p>

                        <div className={theme.smallHeader}>
                            <Trans>Select buyer options to override</Trans>

                            <InfoIconWithTooltip
                                color="light"
                                title={<Trans>Select buyer options to override</Trans>}
                                content={
                                    <Trans>
                                        If this offer overrides any of the buyer options listed below, please select them, so
                                        that they don’t appear as possible options to the buyer.
                                    </Trans>
                                }
                            />
                        </div>
                        <ChangeRequestOverrideOptionSelect
                            onChange={setSelectedOverrides}
                            value={selectedOverrides}
                            residentialUnitId={changeRequest.residentialUnitId}
                        />
                    </SimpleWizardStep>

                    <SimpleWizardStep stepNumber={Step.AddTags} header={<Trans>Tags overview</Trans>}>
                        <p>
                            <Trans>
                                Please make sure that all tags that should be added to this request have been added. Changing
                                tags is done on each quote row.
                            </Trans>
                        </p>
                        <div className={theme.smallHeader}>
                            <Trans>Tags</Trans>
                        </div>
                        <Tags tags={changeRequest.tags} />
                    </SimpleWizardStep>
                    <SimpleWizardStep stepNumber={Step.AddOfferMessage} header={<Trans>Add an offer message</Trans>}>
                        <div className={theme.smallHeader}>
                            <Trans>Add offer message</Trans>

                            <InfoIconWithTooltip
                                color="light"
                                title={<Trans>Add offer message</Trans>}
                                content={<Trans>Add a message to explain the offer to the buyer.</Trans>}
                            />
                        </div>
                        <TextArea
                            autoResize={true}
                            value={note}
                            name="note"
                            className="form-control"
                            placeholder={t`You can add a message to the buyer to explain the offer`}
                            onChange={(e) => setNote(e.target.value)}
                            minRows={5}
                        />
                    </SimpleWizardStep>

                    <SimpleWizardStep stepNumber={Step.PreviewAndSend} header={<Trans>Review and send</Trans>}>
                        <p>
                            <Trans>Please review the offer before you send it to the buyer.</Trans>
                        </p>
                        <div className={theme.smallHeader}>
                            <Trans>Preview offer</Trans>
                            <InfoIconWithTooltip
                                color="light"
                                title={<Trans>Preview offer</Trans>}
                                content={<Trans>Preview of the offer as the buyer will see it.</Trans>}
                            />
                        </div>
                        <div className={theme.previewBox}>
                            <BuyerChangeRequestOffer
                                isPreview
                                changeRequestId={changeRequest.id}
                                onChange={() => null}
                                offer={buyerOffer}
                            />
                        </div>
                        <SlideToggle
                            className={theme.bannerMargin}
                            checked={showSubtotalsToBuyers}
                            onChange={(e) => setShowSubtotalsToBuyers(e.target.checked)}>
                            <Trans>Show subtotals to buyers</Trans>
                        </SlideToggle>
                        <Banner icon={faExclamationCircle} type="info">
                            <Trans>
                                <strong>Please review the offer</strong> to ensure that all participants that need to provide
                                a quote for this offer have been included in this change request prior to sending to the
                                client.
                            </Trans>
                        </Banner>
                    </SimpleWizardStep>

                    <SimpleWizardConfirmStep stepNumber={Step.Confirm} header={<Trans>Sending offer...</Trans>}>
                        <PromiseCheckList className={theme.saveProgress}>
                            {/* <PromiseCheckListItem progress={saveProgress.tags} text={<Trans>Updating tags</Trans>} /> */}
                            <PromiseCheckListItem
                                progress={saveProgress.options}
                                text={<Trans>Updating option overrides</Trans>}
                            />
                            <PromiseCheckListItem progress={saveProgress.offer} text={<Trans>Sending offer</Trans>} />
                        </PromiseCheckList>
                    </SimpleWizardConfirmStep>
                </SimpleWizard>
            </ModalBody>
        </Modal>
    );
};

export default ChangeRequestSendDialog;
